import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { type BrandingConfig, type WhiteLabelSettings, DEFAULT_BRANDING, resolveBranding } from '../types/branding';

interface BrandingContextType {
  branding: BrandingConfig;
  whiteLabelSettings: WhiteLabelSettings | null;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [whiteLabelSettings, setWhiteLabelSettings] = useState<WhiteLabelSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setBranding(DEFAULT_BRANDING);
      setWhiteLabelSettings(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadBranding = async () => {
      try {
        // Try to find a coach record for this user
        const { data: coachData } = await supabase
          .from('coaches')
          .select('whitelabel_enabled, wl_brand_name, wl_logo_url, wl_primary_color, wl_secondary_color, wl_custom_domain, wl_from_email, wl_from_name')
          .eq('user_id', user.id)
          .maybeSingle() as any;

        if (cancelled) return;

        if (coachData) {
          const wl: WhiteLabelSettings = {
            whitelabel_enabled: coachData.whitelabel_enabled ?? false,
            wl_brand_name: coachData.wl_brand_name ?? null,
            wl_logo_url: coachData.wl_logo_url ?? null,
            wl_primary_color: coachData.wl_primary_color ?? null,
            wl_secondary_color: coachData.wl_secondary_color ?? null,
            wl_custom_domain: coachData.wl_custom_domain ?? null,
            wl_from_email: coachData.wl_from_email ?? null,
            wl_from_name: coachData.wl_from_name ?? null,
          };
          setWhiteLabelSettings(wl);
          setBranding(resolveBranding(wl));
        } else {
          // Check if this user is a respondent — if so, find their coach's branding
          const { data: respondentData } = await supabase
            .from('respondents')
            .select('coach_id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (cancelled) return;

          if (respondentData?.coach_id) {
            const { data: coachWl } = await supabase
              .from('coaches')
              .select('whitelabel_enabled, wl_brand_name, wl_logo_url, wl_primary_color, wl_secondary_color, wl_custom_domain, wl_from_email, wl_from_name')
              .eq('id', respondentData.coach_id)
              .maybeSingle() as any;

            if (cancelled) return;

            if (coachWl) {
              const wl: WhiteLabelSettings = {
                whitelabel_enabled: coachWl.whitelabel_enabled ?? false,
                wl_brand_name: coachWl.wl_brand_name ?? null,
                wl_logo_url: coachWl.wl_logo_url ?? null,
                wl_primary_color: coachWl.wl_primary_color ?? null,
                wl_secondary_color: coachWl.wl_secondary_color ?? null,
                wl_custom_domain: coachWl.wl_custom_domain ?? null,
                wl_from_email: coachWl.wl_from_email ?? null,
                wl_from_name: coachWl.wl_from_name ?? null,
              };
              setWhiteLabelSettings(wl);
              setBranding(resolveBranding(wl));
            } else {
              setBranding(DEFAULT_BRANDING);
            }
          } else {
            setBranding(DEFAULT_BRANDING);
          }
        }
      } catch {
        if (!cancelled) setBranding(DEFAULT_BRANDING);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadBranding();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return (
    <BrandingContext.Provider value={{ branding, whiteLabelSettings, isLoading }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}
